import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import MenuContainer from "./menumasterContainer";
import MenuForm from "./menumasterform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  fetchMenusThunk,
  addMenuThunk,
  updateMenuThunk,
  deleteMenuThunk,
  fetchModulesThunk,
  fetchApplicationsThunk,
} from "slices/RBAC/thunk";

import { Link, Navigate, useNavigate } from "react-router-dom";

const selectRBACState = (state: RootState) => (state as any).rbac;

const rbacSelector = createSelector(selectRBACState, (rbac) => ({
  menus: rbac.menus,
  modules: rbac.modules,
  applications: rbac.applications,
  loading: rbac.loading,
}));

const MenuMasterPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { menus, modules, applications, loading } = useSelector(rbacSelector);

  const menuList = menus || [];
  const [filterMenuId, setFilterMenuId] = useState<number | null>(null);
  const [filterParentId, setFilterParentId] = useState<number | null>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);
  const [selectedMenuId, setSelectedMenuId] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<number>(0);
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editMenuData, setEditMenuData] = useState<any>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedMenuIdForDelete, setSelectedMenuIdForDelete] = useState<number | null>(null);

  const loadMenuList = useCallback(() => {
    return dispatch(
      fetchMenusThunk({
        parentid: selectedParentId || 0,
        moduleid: selectedModuleId || 0,
        menuid: selectedMenuId || 0,
        status: selectedStatus,
      })
    );
  }, [dispatch, selectedParentId, selectedModuleId, selectedMenuId, selectedStatus]);

  useEffect(() => {
    dispatch(fetchApplicationsThunk());
    loadMenuList();
  }, [dispatch]);

  useEffect(() => {
    if (selectedApplicationId) {
      dispatch(fetchModulesThunk(selectedApplicationId));
    } else {
      dispatch(fetchModulesThunk(0));
    }
  }, [dispatch, selectedApplicationId]);

  useEffect(() => {
    loadMenuList();
  }, [loadMenuList]);

  const navigate = useNavigate();

  const handleDeleteClick = (id: number) => {
    setSelectedMenuIdForDelete(id);
    setDeleteModalOpen(true);
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        applicationid: isEdit
          ? Yup.number().optional()
          : Yup.number()
            .required("Application is required")
            .min(1, "Application is required"),
        moduleiid: Yup.number()
          .required("Module is required")
          .min(1, "Module is required"),
        parentid: Yup.number()
          .min(0, "Parent Menu must be 0 or greater")
          .optional(),
        title: Yup.string()
          .required("Title is required")
          .matches(/^[a-zA-Z\s]+$/, "Title can contain characters only")
          .max(70, "Maximum 70 characters allowed"),
        description: Yup.string()
          .required("Description is required")
          .matches(/^[a-zA-Z\s]+$/, "Description can contain characters only")
          .max(100, "Maximum 100 characters allowed"),
        routepath: Yup.string()
          .test(
            "routepath-required",
            "Route Path is required when Parent Menu is selected",
            function (value) {
              const { parentid } = this.parent;
              if (parentid && parentid > 0) {
                return !!(value && value.trim().length > 0);
              }
              return true;
            }
          )
          .max(70, "Maximum 70 characters allowed"),
        displayorder: Yup.number()
          .required("Display Order is required")
          .min(1, "Display Order must be at least 1")
          .max(9999, "Maximum 4 digits allowed")
          .test(
            "max-digits",
            "Maximum 4 digits allowed",
            function (value) {
              if (!value) return true;
              return value.toString().length <= 4;
            }
          ),
        status: isEdit
          ? Yup.number().required("Status is required")
          : Yup.number().optional(),
      }),
    [isEdit]
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      applicationid: selectedApplicationId || "",
      moduleiid: "",
      parentid: 0,
      title: "",
      description: "",
      routepath: "",
      displayorder: "",
      status: 1,
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Close modal immediately
      setIsModalOpen(false);
      resetForm();
      const editData = editMenuData;
      const isEditMode = isEdit;
      setIsEdit(false);
      setEditMenuData(null);

      try {
        if (isEditMode && editData) {
          const res = await dispatch(
            updateMenuThunk(editData.menuid, {
              moduleiid: Number(values.moduleiid),
              parentid: Number(values.parentid),
              title: values.title,
              description: values.description,
              routepath: values.routepath,
              displayorder: Number(values.displayorder),
              status: values.status || 1,
            })
          );
          if (!res?.success) {
            toast.error(res?.message || "Failed to update menu");
            return;
          }
          toast.success("Menu updated successfully!");
        } else {
          const res = await dispatch(
            addMenuThunk({
              moduleiid: Number(values.moduleiid),
              parentid: Number(values.parentid),
              title: values.title,
              description: values.description,
              routepath: values.routepath,
              displayorder: Number(values.displayorder),
            })
          );
          if (!res?.success) {
            toast.error(res?.message || "Failed to add menu");
            return;
          }
          toast.success("Menu added successfully!");
        }

        await loadMenuList();
      } catch (err) {
        console.error("Error saving menu:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm({
        values: {
          applicationid: selectedApplicationId || "",
          moduleiid: "",
          parentid: 0,
          title: "",
          description: "",
          routepath: "",
          displayorder: "",
          status: 1,
        },
      });
      setIsEdit(false);
      setEditMenuData(null);
      setSelectedApplicationId(null);
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const handleEditClick = useCallback(
    (row: any) => {
      setIsEdit(true);
      setEditMenuData(row);
      setIsModalOpen(true);

      // Find the module to get the applicationid (menu.moduleiid matches module.moduleid)
      const menuModule = modules.find((mod: any) => mod.moduleid === row.moduleiid);
      const appId = menuModule?.applicationid || null;
      
      if (appId) {
        setSelectedApplicationId(appId);
        // Fetch modules for this specific application to populate the dropdown
        dispatch(fetchModulesThunk(appId));
      }

      validation.setValues({
        applicationid: appId || "",
        moduleiid: row.moduleiid || "",
        parentid: row.parentid || 0,
        title: row.title || "",
        description: row.description || "",
        routepath: row.routepath || "",
        displayorder: row.displayorder || "",
        status: row.status === "Active" ? 1 : 2,
      });
    },
    [validation, modules, dispatch]
  );

  const filteredMenus = useMemo(() => {
    return menuList.filter((menu: any) => {
      if (filterMenuId !== null && menu.menuid !== filterMenuId) return false;
      if (filterParentId !== null && menu.parentid !== filterParentId) return false;
      return true;
    });
  }, [menuList, filterMenuId, filterParentId]);

  const columns = useMemo(
    () => [
      {
        header: "Menu ID",
        accessorKey: "menuid",
        cell: ({ row }: any) => {
          const id = row.original.menuid;
          return `#ID${String(id).padStart(3, "0")}`;
        },
      },
      { header: "Title", accessorKey: "title" },
      { header: "Description", accessorKey: "description" },
      { header: "Route Path", accessorKey: "routepath" },
      { header: "Module", accessorKey: "modulename" },
      { header: "Application", accessorKey: "applicationname" },
      {
        header: "Display Order",
        accessorKey: "displayorder",
        cell: ({ row }: any) => {
          return (
            <div style={{ textAlign: "center" }}>
              {row.original.displayorder}
            </div>
          );
        },
      },
      {
        header: "Parent",
        accessorKey: "parentid",
        cell: ({ row }: any) => {
          const parentId = row.original.parentid;
          if (parentId === 0 || !parentId) {
            return <div style={{ textAlign: "center" }}>—</div>;
          }
          const parentMenu = menuList.find((menu: any) => menu.menuid === parentId);
          return (
            <div style={{ textAlign: "center" }}>
              {parentMenu?.title || "—"}
            </div>
          );
        },
      },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const lowerVal = value?.toString().toLowerCase();
          const statusColor =
            lowerVal === "active" ? "#159E3A" : "#A65F00";

          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
              }}
            >
              {value}
            </span>
          );
        },
      },
      { header: "Created By", accessorKey: "createdby" },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const row = cellProps.row.original;

          return (
            <ul className="list-inline d-flex justify-content-center align-items-center mb-0">
              <li className="list-inline-item" title="Edit">
                <Button
                  color=""
                  className="btn btn-icon btn-soft-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(row);
                  }}
                >
                  <i className="ri-pencil-fill"></i>
                </Button>
              </li>

              {/* Delete */}
              <li className="list-inline-item" title="Delete">
                <Button
                  color=""
                  className="btn btn-icon btn-soft-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(row.menuid);
                  }}
                >
                  <i className="ri-delete-bin-line"></i>
                </Button>
              </li>
            </ul>
          );
        },
      },
    ],
    [handleEditClick, handleDeleteClick]
  );

  // Prepare parent menu options (menus with parentid = 0 can be parents)
  const parentMenuOptions = useMemo(() => {
    const parentMenus = menuList.filter((menu: any) => menu.parentid === 0);
    return parentMenus.map((menu: any) => ({
      label: menu.title,
      value: menu.menuid,
    }));
  }, [menuList]);

  // Prepare menu options for filter (all menus)
  const menuFilterOptions = useMemo(() => {
    return menuList.map((menu: any) => ({
      label: menu.title,
      value: menu.menuid,
    }));
  }, [menuList]);

  // Prepare application options
  const applicationOptions = useMemo(() => {
    if (!Array.isArray(applications)) return [];
    const activeApplications = applications.filter((app: any) => app.status?.toLowerCase() === "active");
    return activeApplications.map((app: any) => ({
      label: app.applicationname,
      value: app.applicationid,
    }));
  }, [applications]);

  // Prepare module options (filtered by selected application)
  const moduleOptions = useMemo(() => {
    if (!Array.isArray(modules)) return [];
    const filteredModules = selectedApplicationId
      ? modules.filter((mod: any) => mod.applicationid === selectedApplicationId)
      : modules;
    const activeModules = filteredModules.filter((mod: any) => mod.status?.toLowerCase() === "active");
    return activeModules.map((mod: any) => ({
      label: mod.modulename,
      value: mod.moduleid,
    }));
  }, [modules, selectedApplicationId]);

  // Handle application change
  const handleApplicationChange = (applicationId: number | null) => {
    setSelectedApplicationId(applicationId);
    validation.setFieldValue("applicationid", applicationId || "");
    validation.setFieldValue("moduleiid", ""); // Reset module when application changes
    if (applicationId) {
      dispatch(fetchModulesThunk(applicationId));
    } else {
      dispatch(fetchModulesThunk(0));
    }
  };

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Menu" pageTitle={["RBAC", "Menus"]} />

        <Row className="align-items-center g-3 pb-4 pe-3">
          <Col md={4}>
            <div style={{ width: "100%" }}>
              <Select
                options={menuFilterOptions}
                placeholder="Filter by Menu"
                value={menuFilterOptions.find((opt: any) => opt.value === filterMenuId) || null}
                onChange={(e: { value: any } | null) => setFilterMenuId(e?.value || null)}
                classNamePrefix="react-select"
                isClearable
              />
            </div>
          </Col>
          <Col md={4}>
            <div style={{ width: "100%" }}>
              <Select
                options={parentMenuOptions}
                placeholder="Filter by Parent"
                value={parentMenuOptions.find((opt: any) => opt.value === filterParentId) || null}
                onChange={(e: { value: any } | null) => setFilterParentId(e?.value || null)}
                classNamePrefix="react-select"
                isClearable
              />
            </div>
          </Col>
          <Col md={4} className="text-end">
            <Button color="primary" onClick={toggleModal}>
              + Add New
            </Button>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  filteredMenus.length > 0 ? (
                    <MenuContainer
                      columns={columns}
                      data={filteredMenus}
                      customPageSize={10000}
                      limit={10000}
                      setPage={() => {}}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">
                      No records found.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          {isEdit ? "Edit Menu" : "Add New Menu"}
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <MenuForm
              validation={validation}
              isEdit={isEdit}
              parentMenuOptions={parentMenuOptions}
              moduleOptions={moduleOptions}
              applicationOptions={applicationOptions}
              onApplicationChange={handleApplicationChange}
            />
          </ModalBody>
          <ModalFooter>
            <Button type="submit" color="primary">
              {isEdit ? "Update" : "Add"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Menu"
        message="Are you sure you want to delete this Menu?"
        onConfirm={async () => {
          if (selectedMenuIdForDelete) {
            setDeleteModalOpen(false);
            const menuid = selectedMenuIdForDelete;
            setSelectedMenuIdForDelete(null);
            try {
              const res = await dispatch(deleteMenuThunk(menuid));
              if (!res?.success) {
                toast.error(res?.message || "Failed to delete menu");
                return;
              }
              toast.success("Menu deleted successfully!");
              await loadMenuList();
            } catch (err) {
              console.error("Error deleting menu:", err);
              toast.error("An error occurred. Please try again.");
            }
          }
        }}
      />
    </div>
  );
};

export default MenuMasterPage;